import React, { useState, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { X, Camera } from 'lucide-react';
import { updateProfile, uploadAvatar } from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../styles/global.css';
import '../styles/Profile.css';

interface Props {
  onClose: () => void;
  currentName: string;
  currentBio: string;
  currentBadge?: string;
  currentSensitiveContent?: boolean;
  currentSocialLinks?: {
    instagram?: string;
    twitter?: string;
    youtube?: string;
    linkedin?: string;
    github?: string;
  };
}

const SOCIAL_FIELDS = [
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/username', icon: '📸' },
  { key: 'twitter',   label: 'Twitter/X',  placeholder: 'https://x.com/username',         icon: '𝕏' },
  { key: 'youtube',   label: 'YouTube',    placeholder: 'https://youtube.com/@channel',    icon: '▶️' },
  { key: 'linkedin',  label: 'LinkedIn',   placeholder: 'https://linkedin.com/in/username',icon: '💼' },
  { key: 'github',    label: 'GitHub',     placeholder: 'https://github.com/username',     icon: '🐙' },
];

const EditProfile = ({ onClose, currentName, currentBio, currentSocialLinks, currentBadge = '', currentSensitiveContent = false }: Props) => {
  const { user, loginUser, token } = useAuth();
  const [name, setName]     = useState(currentName);
  const [bio, setBio]       = useState(currentBio);
  const [socialLinks, setSocialLinks] = useState({
    instagram: currentSocialLinks?.instagram || '',
    twitter:   currentSocialLinks?.twitter   || '',
    youtube:   currentSocialLinks?.youtube   || '',
    linkedin:  currentSocialLinks?.linkedin  || '',
    github:    currentSocialLinks?.github    || '',
  });
  const [avatar, setAvatar]           = useState<string | null>(user?.avatar || null);
  const [badge, setBadge]             = useState(currentBadge);
  const [sensitiveContent, setSensitiveContent] = useState(currentSensitiveContent);
  const [uploading, setUploading]     = useState(false);
  const [loading, setLoading]         = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Image 5MB se badi nahi honi chahiye!');

    setUploading(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result as string;
      setAvatar(base64); // preview
      try {
        const res = await uploadAvatar({ image: base64 });
        setAvatar(res.data.avatar);
        loginUser({ token: token!, user: { ...user!, avatar: res.data.avatar } });
        toast.success('Photo updated!');
      } catch {
        toast.error('Upload failed');
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await updateProfile({ name, bio, socialLinks, badge, sensitiveContent });
      loginUser({ token: token!, user: { ...user!, name: res.data.name, bio: res.data.bio, socialLinks: res.data.socialLinks, avatar: res.data.avatar, badge: res.data.badge, sensitiveContent: res.data.sensitiveContent } });
      toast.success('Profile updated!');
      onClose();
    } catch {
      toast.error('Update failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-card">

        <div className="modal-header">
          <div className="modal-title">Edit Profile</div>
          <button onClick={onClose} className="modal-close"><X size={16} /></button>
        </div>

        {/* Avatar Upload */}
        <div className="avatar-upload-wrap">
          <div className="avatar-upload-circle" onClick={() => fileRef.current?.click()}>
            {avatar ? (
              <img src={avatar} alt="avatar" className="avatar-upload-img" />
            ) : (
              <div className="avatar-upload-placeholder">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="avatar-upload-overlay">
              {uploading ? <div className="avatar-spinner" /> : <Camera size={18} />}
            </div>
          </div>
          <div className="avatar-upload-hint">Click to change photo</div>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
        </div>

        {/* Name */}
        <div style={{ marginBottom: 14 }}>
          <label className="modal-label">Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="modal-input" />
        </div>

        {/* Bio */}
        <div style={{ marginBottom: 20 }}>
          <label className="modal-label">Bio</label>
          <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3}
            placeholder="Tell people about yourself..." className="modal-textarea" />
        </div>

        {/* Social Links */}
        <div className="modal-section-title">Social Links</div>
        {SOCIAL_FIELDS.map(({ key, label, placeholder, icon }) => (
          <div key={key} style={{ marginBottom: 10 }}>
            <label className="modal-label">{icon} {label}</label>
            <input
              type="url"
              value={socialLinks[key as keyof typeof socialLinks]}
              onChange={(e) => setSocialLinks({ ...socialLinks, [key]: e.target.value })}
              placeholder={placeholder}
              className="modal-input"
            />
          </div>
        ))}

        {/* Badge */}
        <div className="modal-section-title" style={{ marginTop: 20 }}>Profile Badge</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {[
            { value: '',          label: 'None' },
            { value: 'verified',  label: '✅ Verified' },
            { value: 'creator',   label: '🎨 Creator' },
            { value: 'pro',       label: '⭐ Pro' },
          ].map(b => (
            <button key={b.value} onClick={() => setBadge(b.value)}
              style={{
                padding: '6px 14px', borderRadius: 20, border: '1px solid var(--border)',
                background: badge === b.value ? 'var(--accent)' : 'var(--bg-alt)',
                color: badge === b.value ? '#fff' : 'var(--text)',
                fontSize: 13, cursor: 'pointer', fontWeight: badge === b.value ? 700 : 400,
              }}>
              {b.label}
            </button>
          ))}
        </div>

        {/* Sensitive Content */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: '12px 16px', background: 'var(--bg-alt)', borderRadius: 10, border: '1px solid var(--border)' }}>
          <label className="toggle">
            <input type="checkbox" checked={sensitiveContent} onChange={e => setSensitiveContent(e.target.checked)} />
            <span className="toggle-slider" />
          </label>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>🔞 Sensitive Content Warning</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Visitors will see a warning before viewing your profile</div>
          </div>
        </div>

        <button onClick={handleSubmit} disabled={loading} className="modal-save-btn" style={{ marginTop: 4 }}>
          {loading ? 'Saving...' : 'Save Changes →'}
        </button>

      </div>
    </div>
  );
};

export default EditProfile;